import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useGetProjectQuery } from "../../../app/services/projects";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import { useProjectFieldSync } from "../../../hooks/useProjectFieldSync";
import NumberStepper from "../../../components/form-item/NumberStepper";
import Toggle from "../../../components/form-item/Toggle";
import TitleBlock from "../../../components/block/TitleBlock";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import SvgLoadingAnimation from "../../../components/loading/index";

const CHECKPOINT_MIN = 1;
const CHECKPOINT_MAX = 999;
const CHECKPOINT_DEFAULT = 5;

const ProjectTotalShots = () => {
  const { projectId } = useParams();
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: projectData, isLoading } = useGetProjectQuery(
    { project_id: projectId },
    { skip: !projectId || !currentUserData }
  );
  const { updateStatus, debouncedSave } = useProjectFieldSync(projectId);

  const [checkPoint, setCheckPoint] = useState(CHECKPOINT_DEFAULT);
  const initializedRef = useRef(false);
  // 「納品物なし」解除時に復元するための直近の非0値を保持する。
  const lastNonZeroRef = useRef(CHECKPOINT_DEFAULT);

  useEffect(() => {
    if (!initializedRef.current && projectData?.data?.project) {
      const value = projectData.data.project.total_shots_count ?? CHECKPOINT_DEFAULT;
      setCheckPoint(value);
      if (value > 0) lastNonZeroRef.current = value;
      initializedRef.current = true;
    }
  }, [projectData]);

  // すでに提出がある場合は「納品物なし」へ変更できない。
  const submittedCount = projectData?.data?.project?.submitted_shots_count ?? 0;
  const hasSubmissions = submittedCount > 0;
  const noDeliverable = checkPoint === 0;

  const handleChange = (value) => {
    setCheckPoint(value);
    if (value > 0) lastNonZeroRef.current = value;
    debouncedSave({ total_shots_count: value });
  };

  const handleToggleNoDeliverable = () => {
    if (hasSubmissions) return;
    if (noDeliverable) {
      const restored = lastNonZeroRef.current || CHECKPOINT_MIN;
      setCheckPoint(restored);
      debouncedSave({ total_shots_count: restored });
    } else {
      lastNonZeroRef.current = checkPoint;
      setCheckPoint(0);
      debouncedSave({ total_shots_count: 0 });
    }
  };

  if (isLoading) return <SvgLoadingAnimation />;

  return (
    <div className="u-col u-w100 u-gap16">
      <TitleBlock
        title="チェックポイント"
        description="成果物をチェックする数を設定できます。2以上に設定すると、進捗率も表示されます。"
      />
      <div className="material-symbols-rounded" style={{ fontSize: "128px" }}>
        check_circle
      </div>
      {!noDeliverable && (
        <>
          <div>
            <label>チェックポイント（{CHECKPOINT_MIN}-{CHECKPOINT_MAX}）</label>
          </div>
          <NumberStepper
            value={checkPoint}
            min={CHECKPOINT_MIN}
            max={CHECKPOINT_MAX}
            onChange={handleChange}
            aria-label="チェックポイント"
          />
        </>
      )}
      <StatusIndicator status={updateStatus} />
      <div
        className="u-w100"
        style={hasSubmissions ? { opacity: 0.4, pointerEvents: "none" } : undefined}
      >
        <Toggle
          value={noDeliverable}
          onClick={hasSubmissions ? undefined : handleToggleNoDeliverable}
        >
          納品物なし
        </Toggle>
      </div>
      {hasSubmissions && (
        <p className="bodytext-13 u-text-light-gray">
          すでに提出があるため変更できません。
        </p>
      )}
    </div>
  );
};

export default ProjectTotalShots;
